import { useSelector } from 'react-redux';
import { selectIsWhatsAppNewCharLimitsFeatureFlagOn } from 'core/userContext';
import { TemplateType } from 'constants/templateTypes';
import { WhatsAppContentDefinition } from 'modules/graphql/types';
import { useRef } from 'react';
import { FormField } from 'ui/formField';
import { NegativeFill } from 'ui/icons';
import { colors } from 'ui/theme';
import { constructErrorInlineText } from '../../common';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { WhatsAppRestrictionText } from '../../common/RestrictionText';
import { FieldNames, MediumDataKey } from '../../common/types';
import { constructFieldErrors, getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import {
  NEW_MAX_MESSAGE_LENGTH_BUSINESS,
  MAX_MESSAGE_LENGTH_BUSINESS,
  MAX_MESSAGE_LENGTH_USER,
  SETTINGS_DISPLAY_CONTENT,
} from '../constants';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';
import { TextAreaField } from './components/TextAreaField';

export const WHATSAPP_MESSAGE: MediumDataKey = 'message';

export const MessageTextArea = () => {
  const isNewCharLimit = useSelector(selectIsWhatsAppNewCharLimitsFeatureFlagOn);
  const maxBusinessCharLimit = isNewCharLimit
    ? NEW_MAX_MESSAGE_LENGTH_BUSINESS
    : MAX_MESSAGE_LENGTH_BUSINESS;
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField, initialTemplate } =
    useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode) && isEditableWhatsAppTemplate;
  const isBusinessInitiated = liveTemplate?.templateType === TemplateType.BusinessInitiated;

  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  const restrictionTextLabel = (
    <WhatsAppRestrictionText
      maxChars={isBusinessInitiated ? maxBusinessCharLimit : MAX_MESSAGE_LENGTH_USER}
      textContent={whatsAppData?.message || ''}
    />
  );

  const { hasError: messageError, hasWarning: messageWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.MESSAGE,
  });
  const showError = initialTemplate?.createdAt !== initialTemplate?.updatedAt && messageError;

  const messageErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: showError,
    hasWarning: messageWarning,
  });

  return (
    <FormField
      data-test="whats-app-message-form-field"
      errorText={messageErrorText}
      warningText={messageErrorText}
      hasError={showError}
      hasWarning={messageWarning}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.message.labelText}
      restrictionText={restrictionTextLabel}
      helpText={
        isEditModeVisible &&
        !showError &&
        'Supports text, emojis, and merge parameters. To format, highlight the text and select a formatting option.'
      }
    >
      {isEditModeVisible ? (
        <TextAreaField
          hasError={showError}
          mediumRef={messageRef}
          mediumDataKey={WHATSAPP_MESSAGE}
          mediumDataValue={whatsAppData.message}
          hasSecondaryText
          onChangeHandler={(newMessage: string) =>
            setCurrentMediumField(WHATSAPP_MESSAGE, newMessage)
          }
          maxCharacters={
            isBusinessInitiated ? MAX_MESSAGE_LENGTH_BUSINESS : MAX_MESSAGE_LENGTH_USER
          }
          isUnset={!whatsAppData.message}
          showMergeTag
          showMarkdownOptions
        />
      ) : (
        <SettingsReadOnlyText $hasError={showError}>
          {showError && <NegativeFill color={colors.red100} />}
          {whatsAppData.message || SETTINGS_DISPLAY_CONTENT.unsetText}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
